import { useEffect, useState } from 'react';
import { heuresPrevuesApi } from '../../api/resources';
import { Card } from '../../components/ui/Card.jsx';
import { fmtH } from '../../lib/currency';
import { dateSuisse } from '../../lib/dates';
import { bilanHeures } from '../../lib/heures-chantier';
import { tr, trf } from '../../lib/i18n';
import { C } from '../../theme/palette';

/**
 * Les heures du chantier : prévues et faites, qui dépasse son contrat, et si
 * les horaires sont respectés (arrivées en retard, pointages hors du
 * chantier). Le patron, le directeur et le technicien du chantier la voient.
 *
 * @param {object} props
 * @param {object} props.chantier le chantier (id, pointages)
 * @param {object[]} props.employes les fiches (heuresJour, heureDebut)
 * @param {boolean} [props.peutModifier] taper les heures prévues
 */
export function HeuresPrevues({ chantier, employes, peutModifier = true }) {
  const [h, setH] = useState(null);
  const [saisie, setSaisie] = useState('');
  const [edite, setEdite] = useState(false);
  const [erreur, setErreur] = useState('');
  const id = chantier?.id;

  useEffect(() => {
    if (typeof id !== 'number') return undefined;
    let vivant = true;
    Promise.resolve()
      .then(() => heuresPrevuesApi.lit(id))
      .then((r) => vivant && setH(r || null))
      .catch(() => {});
    return () => {
      vivant = false;
    };
  }, [id]);

  const bilan = bilanHeures(chantier?.pointages, employes);
  const prevues = h?.prevues != null ? Number(h.prevues) : null;
  const ecart = prevues != null ? Math.round((bilan.faites - prevues) * 100) / 100 : null;
  const depasse = ecart != null && ecart > 0;
  const pct = prevues ? Math.min(100, Math.round((bilan.faites / prevues) * 100)) : 0;

  const enregistre = async () => {
    setErreur('');
    const v = saisie.trim() === '' ? null : Number(saisie.replace(',', '.'));
    if (v != null && !(v >= 0)) {
      setErreur(tr('Tape un nombre d’heures.'));
      return;
    }
    try {
      setH(await heuresPrevuesApi.ecrit(id, v));
      setEdite(false);
    } catch (e) {
      setErreur(e?.message || tr('Les heures prévues n’ont pas pu être enregistrées.'));
    }
  };

  const titre = {
    fontSize: 10,
    fontWeight: 800,
    color: C.muted,
    letterSpacing: 0.4,
    marginBottom: 6,
  };
  return (
    <Card style={{ marginBottom: 10 }}>
      <div style={{ padding: '11px 13px' }} data-heures-prevues>
        <div style={titre}>{tr('HEURES PRÉVUES ET FAITES')}</div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            gap: 8,
          }}
        >
          <span style={{ fontSize: 22, fontWeight: 900, color: depasse ? C.red : C.ink }}>
            {fmtH(bilan.faites)}
          </span>
          <span style={{ fontSize: 12, color: C.grey }}>
            {prevues != null
              ? trf('prévues : {0}', fmtH(prevues))
              : tr('pas encore d’heures prévues')}
          </span>
        </div>
        {prevues != null && (
          <div
            style={{
              height: 10,
              borderRadius: 99,
              background: C.border,
              overflow: 'hidden',
              margin: '6px 0',
            }}
          >
            <div
              style={{ width: pct + '%', height: '100%', background: depasse ? C.red : C.green }}
            />
          </div>
        )}
        {ecart != null && (
          <div style={{ fontSize: 12, fontWeight: 800, color: depasse ? C.red : C.green }}>
            {depasse
              ? trf(
                  '⚠️ {0} de plus que prévu (+{1} %)',
                  fmtH(ecart),
                  Math.round((ecart / prevues) * 100),
                )
              : trf('✓ Il reste {0} sur les heures prévues', fmtH(-ecart))}
          </div>
        )}
        <div style={{ fontSize: 10.5, color: C.grey, marginTop: 4, lineHeight: 1.45 }}>
          {h?.saisies != null
            ? tr('Prévues : tapées à la main.')
            : Number(h?.offre) > 0
              ? tr('Prévues : les lignes en heures de l’offre et des avenants.')
              : tr('L’offre ne donne pas d’heures (m², forfait) : tape les heures prévues.')}
        </div>
        {peutModifier && typeof id === 'number' && (
          <div style={{ marginTop: 6 }}>
            {edite ? (
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                <input
                  aria-label={tr('Heures prévues')}
                  inputMode="decimal"
                  value={saisie}
                  placeholder={tr('ex. 320')}
                  onChange={(e) => setSaisie(e.target.value)}
                  style={{
                    width: 90,
                    padding: 8,
                    borderRadius: 8,
                    border: '1px solid ' + C.border,
                    fontSize: 13,
                  }}
                />
                <button
                  type="button"
                  onClick={enregistre}
                  style={{
                    padding: '8px 12px',
                    borderRadius: 8,
                    border: 'none',
                    background: C.teal,
                    color: C.white,
                    fontWeight: 800,
                  }}
                >
                  {tr('Enregistrer')}
                </button>
                <button
                  type="button"
                  onClick={() => setEdite(false)}
                  style={{
                    padding: '8px 12px',
                    borderRadius: 8,
                    border: '1px solid ' + C.border,
                    background: C.white,
                  }}
                >
                  {tr('Annuler')}
                </button>
                <div style={{ flexBasis: '100%', fontSize: 10.5, color: C.grey }}>
                  {tr('Laisse vide pour reprendre les heures de l’offre.')}
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => {
                  setSaisie(h?.saisies != null ? String(h.saisies) : '');
                  setEdite(true);
                }}
                style={{
                  padding: '6px 10px',
                  borderRadius: 8,
                  border: '1px solid ' + C.teal,
                  background: C.white,
                  color: C.teal,
                  fontSize: 11.5,
                  fontWeight: 800,
                }}
              >
                ✎ {tr('Taper les heures prévues')}
              </button>
            )}
            {erreur && (
              <div role="alert" style={{ fontSize: 11.5, color: C.red, marginTop: 4 }}>
                {erreur}
              </div>
            )}
          </div>
        )}

        {bilan.parEmploye.length > 0 && (
          <>
            <div style={{ ...titre, marginTop: 12 }}>{tr('PAR EMPLOYÉ')}</div>
            {bilan.parEmploye.map((e) => (
              <div key={e.empId ?? e.nom} style={{ padding: '3px 0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5 }}>
                  <span>{e.nom}</span>
                  <b>{fmtH(e.heures)}</b>
                </div>
                {e.joursAuDela > 0 && (
                  <div style={{ fontSize: 11, color: '#8A5A00' }}>
                    {trf('{0} journée(s) au-delà de son contrat', e.joursAuDela)}
                  </div>
                )}
              </div>
            ))}
          </>
        )}

        {bilan.parEmploye.length > 0 && (
          <>
            <div style={{ ...titre, marginTop: 12 }}>{tr('HORAIRES RESPECTÉS ?')}</div>
            {bilan.retards.length === 0 && bilan.horsChantier.length === 0 && (
              <div style={{ fontSize: 12.5, color: C.green }}>
                {tr('✓ Arrivées à l’heure, pointages sur le chantier.')}
              </div>
            )}
            {bilan.retards.slice(0, 8).map((r, i) => (
              <div key={'r' + i} style={{ fontSize: 12, color: '#8A5A00' }}>
                {trf('⚠️ {0} : arrivée à {1} le {2}', r.nom, r.entree, dateSuisse(r.date))}
              </div>
            ))}
            {bilan.retards.length > 8 && (
              <div style={{ fontSize: 11.5, color: C.grey }}>
                {trf('… et {0} autre(s) retard(s)', bilan.retards.length - 8)}
              </div>
            )}
            {bilan.horsChantier.slice(0, 8).map((r, i) => (
              <div key={'g' + i} style={{ fontSize: 12, color: '#8A5A00' }}>
                {trf('📍 {0} : pointage hors du chantier le {1}', r.nom, dateSuisse(r.date))}
              </div>
            ))}
          </>
        )}
      </div>
    </Card>
  );
}
